import {useCallback, useEffect, useMemo, useRef} from "react";
import {useSyncedRef} from "../../../../../../misc/SyncedRef";
import {EventEmitter, sleep} from "nate-react-api-helpers";
import {TID} from "../../../../../../misc/scroller/Table";

export function useMemoryTableFetcher2<T extends TID>(value: T[], onChangeFx: (v: T[]) => any) {

    const fs = useMemoryTableFetcher<T[], void>(value, onChangeFx);
    const onChangeRef = useRef(async (t: T) => {
        return t;
    });

    onChangeRef.current = async (v: T) => {
        let remove = false;
        let update = value.map(h => {
            if (h.id === v.id) {
                if("archived" in v && v.archived) {
                    remove = true;
                }

                return Object.assign({}, v, {
                    updatedAt: new Date().toISOString(),
                });
            }

            return h;
        });

        if(remove) {
            update = update.filter(h => h.id !== v.id);
        }

        if(!v.id || v.id <= 0) {
            v = Object.assign({}, v, {
                id: Date.now(),
                updatedAt: new Date().toISOString(),
            })

            update.push(v);
        }

        fs.onChange(update);
        return v;
    }

    return {
        onChange: (v: T) => onChangeRef.current(v),
        fetch: fs.fetch,
    }
}

export function useMemoryTableFetcher<T, U>(value: T, onChangeFx: (v: T) => U) {
    const waitForChangeRef = useRef<null | Promise<any>>();
    const valueRef = useSyncedRef(value);
    const valueChanged = useMemo(() => new EventEmitter<T>(), []);

    useEffect(() => {
        valueChanged.emit(value)
    }, [value, valueChanged]);

    const fetch = useCallback(async () => {
        if(waitForChangeRef.current) {
            await waitForChangeRef.current;
        }

        return valueRef.current;
    }, [valueRef]);

    const onChangeRef = useSyncedRef(onChangeFx);

    const onChange = useCallback((v: T) => {
        waitForChangeRef.current = Promise.race([
            waitForChange(valueChanged),
            sleep(2000) // timeout
        ]);

        return onChangeRef.current(v);
    }, [onChangeRef, valueChanged]);

    return {
        fetch,
        onChange,
    }
}

function waitForChange<T>(e: EventEmitter<T>) {
    return new Promise<void>((resolve, reject) => {
        try {
            const sub = e.subscribe(s => {
                resolve();
                sub.cancel();
            })
        } catch (e) {
            reject(e);
        }
    })
}